/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  HelpCircle, LayoutGrid, Layers, Tag, Grid2X2, MessageSquare, Share2,
  Search, Filter, Sparkles, Keyboard, CreditCard, Moon, Smartphone,
  GanttChartSquare, Info, Activity, ShieldCheck, Bell,
  LayoutTemplate, Paperclip, LifeBuoy, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

const SECTIONS = [
  { id: "intro",        icon: HelpCircle,       title: "Bienvenida" },
  { id: "whats-new",    icon: Sparkles,         title: "Novedades" },
  { id: "templates",    icon: LayoutTemplate,   title: "Plantillas" },
  { id: "workspaces",   icon: Layers,           title: "Workspaces" },
  { id: "boards",       icon: LayoutGrid,       title: "Boards y listas" },
  { id: "board-details", icon: Info,            title: "Ficha del tablero" },
  { id: "cards",        icon: Sparkles,         title: "Tarjetas" },
  { id: "attachments",  icon: Paperclip,        title: "Adjuntos" },
  { id: "labels",       icon: Tag,              title: "Etiquetas" },
  { id: "views",        icon: Grid2X2,          title: "3 vistas: Rolls, Sprints, Matriz" },
  { id: "sprints",      icon: GanttChartSquare, title: "Vista Sprints (timeline)" },
  { id: "matrix",       icon: Grid2X2,          title: "Matriz de prioridades" },
  { id: "comments",     icon: MessageSquare,    title: "Comentarios" },
  { id: "share",        icon: Share2,           title: "Compartir boards" },
  { id: "activity",     icon: Activity,         title: "Actividad y registro" },
  { id: "search",       icon: Search,           title: "Búsqueda y filtros" },
  { id: "responsive",   icon: Smartphone,       title: "Móvil y responsive" },
  { id: "theme",        icon: Moon,             title: "Modo oscuro" },
  { id: "shortcuts",    icon: Keyboard,         title: "Atajos de teclado" },
  { id: "billing",      icon: CreditCard,       title: "Planes, prueba y PayPal" },
  { id: "privacy",      icon: ShieldCheck,      title: "Privacidad y términos" },
];

function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 space-y-3" data-testid={`help-section-${id}`}>
      <h2 className="font-display text-2xl font-semibold flex items-center gap-2">
        <Icon className="h-5 w-5 text-blue-500" /> {title}
      </h2>
      <div className="text-sm leading-relaxed text-foreground/90 space-y-2 [&_strong]:font-semibold [&_li]:my-0.5 [&_ul]:list-disc [&_ul]:pl-5">
        {children}
      </div>
    </section>
  );
}

export default function Help() {
  const [active, setActive] = useState("intro");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0.1 },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="h-full overflow-y-auto kanban-scroll" data-testid="help-page">
      <div className="max-w-6xl mx-auto p-6 md:p-10 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8">
        {/* TOC */}
        <aside className="md:sticky md:top-6 self-start">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">Guía de uso</p>
          <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible kanban-scroll pb-2 md:pb-0">
            {SECTIONS.map((s) => {
              const I = s.icon;
              return (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`help-toc-${s.id}`}
                  className={cn(
                    "px-3 py-2 rounded-lg text-sm inline-flex items-center gap-2 transition flex-none",
                    active === s.id
                      ? "bg-blue-500/15 text-blue-600 dark:text-blue-300 font-medium"
                      : "text-muted-foreground hover:bg-white/40 dark:hover:bg-white/5"
                  )}
                >
                  <I className="h-4 w-4" />
                  <span className="whitespace-nowrap">{s.title}</span>
                </a>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <article className="space-y-10 animate-fade-in-up">
          <header>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Centro de ayuda</p>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">¿Cómo usar Sprintroll?</h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">
              Esta guía te lleva paso a paso por todas las funciones de la plataforma. Está pensada
              tanto para uso individual como para equipos pequeños.
            </p>
          </header>

          {/* Support CTA banner */}
          <Link
            to="/support"
            data-testid="help-support-banner"
            className="group block rounded-2xl overflow-hidden bg-gradient-to-br from-blue-600 via-blue-500 to-sky-500 text-white p-6 md:p-7 shadow-lg shadow-blue-500/30 hover:shadow-xl hover:-translate-y-0.5 transition"
          >
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl bg-white/20 grid place-items-center flex-none backdrop-blur">
                <LifeBuoy className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-[0.15em] font-semibold opacity-90">Centro de soporte</p>
                <h2 className="font-display text-xl md:text-2xl font-semibold mt-1">
                  ¿Necesitas ayuda personal?
                </h2>
                <p className="text-sm opacity-95 mt-1 max-w-lg">
                  Abre un ticket de soporte con adjuntos y capturas. Te contestamos por email y aquí mismo.
                </p>
              </div>
              <ChevronRight className="h-5 w-5 mt-3 flex-none opacity-80 group-hover:translate-x-1 transition" />
            </div>
          </Link>

          <Section id="intro" icon={HelpCircle} title="Bienvenida">
            <p>Sprintroll es un gestor de proyectos visual. Organizas tu trabajo en <strong>workspaces → boards → listas → tarjetas</strong>:</p>
            <ul>
              <li><strong>Workspace</strong>: tu espacio principal (personal o de equipo).</li>
              <li><strong>Board</strong>: un proyecto o flujo de trabajo dentro del workspace.</li>
              <li><strong>Listas</strong>: las etapas o columnas del board (por ejemplo "Backlog", "En progreso", "Hecho").</li>
              <li><strong>Tarjetas</strong>: las tareas concretas que se mueven entre listas.</li>
            </ul>
            <p>Cada cuenta nueva recibe automáticamente un workspace "Mi Workspace" y 3 boards de ejemplo para empezar a explorar.</p>
          </Section>

          <Section id="whats-new" icon={Sparkles} title="Novedades">
            <p>Cosas nuevas que estrenamos recientemente:</p>
            <ul>
              <li><strong>Plantillas de tableros</strong> 🆕 — al crear un board, elige entre 7 plantillas pre-cargadas (mudanza, fiesta, vacaciones, negocio, marketing, producto, ventas) o empieza desde cero.</li>
              <li><strong>Adjuntos en tarjetas</strong> 🆕 — sube imágenes y archivos hasta 10 MB con preview inline y descarga directa.</li>
              <li><strong>Ficha de detalles del tablero</strong> — botón <em>Detalles</em> en el header del board con autor, integrantes, métricas y última actualización.</li>
              <li><strong>Vista Sprints</strong> — timeline tipo Gantt para ver tareas con fecha de inicio y entrega.</li>
              <li><strong>Tres vistas sobre la misma data</strong> — <em>Rolls</em> (kanban), <em>Sprints</em> (timeline) y <em>Matriz</em> (prioridades). Alterna en cualquier momento sin perder cambios.</li>
              <li><strong>Boards compartidos</strong> — invitación por email o por enlace, con miembros separados en el sidebar.</li>
              <li><strong>Pagos con PayPal en producción</strong> — suscripción mensual o anual con verificación automática.</li>
              <li><strong>Plan gratuito para siempre</strong> — sin tarjeta, con 2 workspaces, 2 tableros (incluyendo los compartidos) y 10 MB de almacenamiento. Actualiza cuando lo necesites.</li>
              <li><strong>Emails transaccionales</strong> — bienvenida, invitaciones, recordatorios de prueba y comprobantes de pago.</li>
              <li><strong>Exportar y eliminar cuenta</strong> — descarga toda tu data en JSON o borra tu cuenta en cualquier momento desde Configuración.</li>
              <li><strong>Modo oscuro</strong> — preferencia guardada por usuario y dispositivo.</li>
            </ul>
          </Section>

          <Section id="templates" icon={LayoutTemplate} title="Plantillas de tableros">
            <p>Cuando creas un board nuevo te preguntamos cómo quieres empezar:</p>
            <ul>
              <li><strong>Usar plantilla</strong> — eliges una de nuestras plantillas oficiales y la usas tal cual o le cambias el nombre.</li>
              <li><strong>Empezar desde cero</strong> — un board vacío al que tú le das estructura.</li>
            </ul>
            <p>Plantillas oficiales disponibles:</p>
            <ul>
              <li><strong>Cambio de casa</strong> — 17 tareas en 5 etapas, desde planeación hasta la primera noche en tu nuevo hogar.</li>
              <li><strong>Organizar una fiesta</strong> — invitados, logística, comida y día del evento listos para coordinar.</li>
              <li><strong>Planear vacaciones</strong> — investigación, presupuesto, reservas, preparativos y empaque.</li>
              <li><strong>Iniciar un negocio</strong> — de la idea al lanzamiento, con pasos legales y fiscales mexicanos.</li>
              <li><strong>Estrategia de marketing</strong> — análisis, estrategia, contenido, canales y medición.</li>
              <li><strong>Desarrollo de un producto</strong> — discovery, diseño, desarrollo, QA y lanzamiento.</li>
              <li><strong>Estrategia de ventas</strong> — pipeline B2B completo desde prospección hasta post-venta.</li>
            </ul>
            <p>Al elegir una plantilla verás una <strong>vista previa horizontal</strong> con sus listas y primeras tareas. Si te convence, le asignas un workspace, opcionalmente le cambias el nombre y le das a <em>Usar esta plantilla</em>. En segundos tienes el board listo para personalizar — todas las tareas se vuelven editables como cualquier otra.</p>
            <p><strong>Para Super Admins:</strong> dentro de cualquier board hay un botón <em>Convertir en plantilla</em> en el header. Cualquier tablero puede convertirse en plantilla oficial disponible para todos los usuarios.</p>
          </Section>

          <Section id="workspaces" icon={Layers} title="Workspaces">
            <p>Para <strong>crear un workspace</strong>:</p>
            <ul>
              <li>En el menú lateral, junto a "WORKSPACES", toca el botón <strong>+</strong>.</li>
              <li>Asigna nombre, descripción opcional y un color identificador.</li>
            </ul>
            <p>Tu workspace activo aparece resaltado en el sidebar. Puedes crear varios workspaces y cambiar entre ellos en cualquier momento.</p>
          </Section>

          <Section id="boards" icon={LayoutGrid} title="Boards y listas">
            <p><strong>Crear un board</strong>: usa el botón "Crear board" en el dashboard o dentro de un workspace. Elige color de portada y descripción.</p>
            <p><strong>Por defecto cada board incluye 4 listas</strong>: Backlog, Por hacer, En progreso y Hecho. Puedes añadir, renombrar, reordenar y eliminar listas.</p>
            <ul>
              <li>Añadir lista: botón "Añadir lista" al final del board.</li>
              <li>Renombrar lista: toca el nombre de la lista para editarlo.</li>
              <li>Reordenar listas: arrastra el encabezado horizontalmente.</li>
              <li>Eliminar lista: menú "…" de la lista → "Eliminar lista".</li>
            </ul>
            <p>Toca la estrella del board para <strong>destacarlo</strong>; los destacados aparecen en la parte superior del dashboard.</p>
          </Section>

          <Section id="board-details" icon={Info} title="Ficha de detalles del tablero">
            <p>Cada board tiene una <strong>ficha de detalles</strong> que resume toda su información clave de un vistazo. Para abrirla:</p>
            <ul>
              <li>Entra a cualquier board.</li>
              <li>En el header, toca el botón <strong>Detalles</strong> (icono <em>ⓘ</em>).</li>
            </ul>
            <p>La ficha muestra:</p>
            <ul>
              <li><strong>Título y descripción ampliada</strong> del tablero.</li>
              <li><strong>Autor</strong>: avatar, nombre y email de quien lo creó.</li>
              <li><strong>Fechas</strong>: creación y última actualización (con hora exacta y descripción relativa, p.ej. "hace 2 h").</li>
              <li><strong>Métricas</strong>: cantidad de tareas activas, número de listas e integrantes.</li>
              <li><strong>Estado</strong>: si es <em>Privado</em>, <em>Colaborativo</em>, <em>Archivado</em> o <em>Destacado</em>.</li>
              <li><strong>Integrantes</strong>: para boards colaborativos, lista completa con nombre, email, fecha de invitación y estado (Activo o Pendiente).</li>
            </ul>
            <p>Es ideal para auditorías rápidas, reportes a stakeholders o cuando un nuevo miembro se incorpora y quiere entender el contexto del tablero.</p>
          </Section>

          <Section id="cards" icon={Sparkles} title="Tarjetas (cards)">
            <p>Las tarjetas representan tareas. Para crearlas:</p>
            <ul>
              <li>Dentro de cualquier lista, toca "Añadir tarjeta" y escribe el título.</li>
              <li>Para abrir el detalle de una tarjeta, haz clic sobre ella.</li>
            </ul>
            <p>Dentro del detalle de una tarjeta puedes editar:</p>
            <ul>
              <li>Título y descripción.</li>
              <li><strong>Fecha de inicio</strong> y <strong>fecha límite</strong> (la primera alimenta la vista Sprints).</li>
              <li>Prioridad: <em>Urgente</em> e <em>Importante</em> (alimentan la <em>Matriz de prioridades</em>).</li>
              <li>Etiquetas (por board).</li>
              <li>Checklist con progreso visual.</li>
              <li>Comentarios con formato Markdown.</li>
              <li>Personas asignadas (en boards colaborativos).</li>
            </ul>
            <p><strong>Drag &amp; drop</strong>: las tarjetas se arrastran entre listas para reordenarlas. Si prefieres teclado: enfoca una tarjeta, presiona <strong>Espacio</strong>, muévete con las flechas y vuelve a presionar <strong>Espacio</strong> para soltar.</p>
          </Section>

          <Section id="attachments" icon={Paperclip} title="Adjuntos en tarjetas">
            <p>Cualquier tarjeta puede llevar imágenes y archivos para conservar contexto, evidencias y entregables en un solo lugar:</p>
            <ul>
              <li>Abre la tarjeta y busca la sección <strong>Adjuntos</strong>.</li>
              <li>Toca <strong>Adjuntar</strong> para elegir uno o varios archivos, o simplemente <strong>arrástralos</strong> al área marcada.</li>
              <li>Las imágenes muestran <strong>preview inline</strong> directamente en la tarjeta; los demás archivos aparecen con icono.</li>
              <li>Toca cualquier adjunto para verlo a tamaño completo o descargarlo.</li>
            </ul>
            <p>Restricciones y notas:</p>
            <ul>
              <li>Tamaño máximo: <strong>10 MB por archivo</strong>.</li>
              <li>Soportado: imágenes (jpg/png/webp/heic/gif), PDF, Word, Excel, PowerPoint, CSV, TXT, MP4, MOV, ZIP y más.</li>
              <li>Quien sube el archivo o el dueño del tablero pueden eliminarlo.</li>
              <li>Cada acción queda en el registro de Actividad.</li>
            </ul>
          </Section>

          <Section id="labels" icon={Tag} title="Etiquetas">
            <p>Cada board incluye etiquetas por defecto orientadas a tu flujo de trabajo:</p>
            <ul>
              <li><strong>Planeación</strong>, <strong>Desarrollo</strong>, <strong>Integración</strong>, <strong>Revisión</strong>, <strong>Medición</strong>, <strong>Ajustes</strong>.</li>
            </ul>
            <p>Para asignar o crear nuevas etiquetas:</p>
            <ul>
              <li>Abre la tarjeta y toca <strong>Etiquetas</strong>.</li>
              <li>Marca las que quieras asignar o crea una nueva escribiendo su nombre y eligiendo color.</li>
            </ul>
            <p>Las etiquetas aparecen como pequeñas barras de color en la parte superior de cada tarjeta.</p>
          </Section>

          <Section id="views" icon={Grid2X2} title="Tres vistas sobre la misma data">
            <p>Sprintroll te da <strong>3 formas de ver el mismo board</strong> sin duplicar información:</p>
            <ul>
              <li><strong>Rolls</strong> — vista clásica tipo kanban con listas horizontales y arrastre de tarjetas.</li>
              <li><strong>Sprints</strong> — timeline tipo Gantt con las tareas calendarizadas entre fecha de inicio y entrega.</li>
              <li><strong>Matriz</strong> — cuadrícula de Eisenhower con 4 cuadrantes (Urgente/Importante).</li>
            </ul>
            <p>Alterna entre vistas con los botones en el header del board. Los cambios en una vista se sincronizan en las demás.</p>
          </Section>

          <Section id="sprints" icon={GanttChartSquare} title="Vista Sprints (timeline)">
            <p>La vista <strong>Sprints</strong> es ideal para planificar trabajo en el tiempo y detectar solapamientos. Funciona como un Gantt simplificado:</p>
            <ul>
              <li>Cada tarjeta aparece como una barra horizontal posicionada entre su <strong>fecha de inicio</strong> y su <strong>fecha de entrega</strong>.</li>
              <li>El color de la barra coincide con la etiqueta principal de la tarjeta.</li>
              <li>Las tarjetas sin fecha de inicio no aparecen en esta vista — añádelas desde el detalle de la tarjeta.</li>
              <li>Toca cualquier barra para abrir el detalle de la tarjeta.</li>
            </ul>
            <p>Para activarla: en el header del board, toca <strong>Sprints</strong>.</p>
          </Section>

          <Section id="matrix" icon={Grid2X2} title="Matriz de prioridades">
            <p>Vista alternativa que organiza tus tarjetas según su <strong>urgencia</strong> e <strong>importancia</strong>, dividida en 4 cuadrantes:</p>
            <ul>
              <li>🔴 <strong>Hacer ahora</strong> — Urgente · Importante.</li>
              <li>🟦 <strong>Programar</strong> — Importante · No urgente.</li>
              <li>🟡 <strong>Delegar</strong> — Urgente · No importante.</li>
              <li>⚪ <strong>Posponer</strong> — No urgente · No importante.</li>
            </ul>
            <p>Para activarla: en cualquier board, alterna el botón <strong>Matriz</strong> en el encabezado.</p>
            <p>Puedes <strong>arrastrar tarjetas entre cuadrantes</strong> — los valores de "Urgente" e "Importante" en la tarjeta se actualizan automáticamente, y el badge de prioridad se sincroniza en la vista Rolls.</p>
          </Section>

          <Section id="comments" icon={MessageSquare} title="Comentarios">
            <p>Cada tarjeta tiene un hilo de comentarios con soporte de <strong>formato Markdown</strong>:</p>
            <ul>
              <li><strong>Negrita</strong> y <em>cursiva</em>.</li>
              <li>Listas con viñetas o numeradas.</li>
              <li>Citas, bloques de código y enlaces.</li>
            </ul>
            <p>Usa la barra de herramientas sobre el cuadro de texto para insertar formato sin escribir Markdown manualmente.</p>
            <p>Puedes <strong>editar</strong> o <strong>eliminar</strong> cualquier comentario propio pasando el ratón sobre él y usando los iconos ✏️ y 🗑️ que aparecen.</p>
          </Section>

          <Section id="share" icon={Share2} title="Compartir boards">
            <p>Comparte un tablero con otras personas de dos formas:</p>
            <ul>
              <li><strong>Por email</strong>: en el header del board toca "Compartir" y agrega uno o varios correos. Si la persona aún no tiene cuenta, la invitación queda <em>pendiente</em> y se acepta automáticamente la primera vez que se loguea con ese email.</li>
              <li><strong>Con enlace</strong>: activa el toggle "Enlace de invitación" y copia la URL. Cualquiera con ese enlace puede unirse como editor.</li>
            </ul>
            <p>En el sidebar, los boards compartidos contigo aparecen en una sección separada con avatares de los integrantes. Los boards muestran etiquetas que indican su estado:</p>
            <ul>
              <li><strong>Colaborativo</strong> — un board tuyo en el que ya hay invitados.</li>
              <li><strong>Compartido conmigo</strong> — un board de otro usuario al que tienes acceso.</li>
            </ul>
            <p>Como propietario, puedes quitar miembros desde el diálogo de compartir, o consultarlos en cualquier momento desde la <strong>ficha de detalles del tablero</strong>.</p>
          </Section>

          <Section id="activity" icon={Activity} title="Actividad y registro">
            <p>Cada board mantiene un <strong>registro de actividad</strong> con todos los cambios importantes: creación y movimiento de tarjetas, comentarios, cambios de etiquetas, invitaciones, etc.</p>
            <ul>
              <li>Abre el panel desde el botón <strong>Actividad</strong> en el header del board.</li>
              <li>Muestra quién hizo qué y cuándo, en orden cronológico inverso.</li>
              <li>La <strong>última actualización</strong> también aparece en la ficha de detalles del tablero.</li>
            </ul>
          </Section>

          <Section id="search" icon={Search} title="Búsqueda y filtros">
            <p>La caja de <strong>búsqueda global</strong> en la parte superior encuentra tarjetas y boards por título o descripción. Dentro del popover puedes acotar el alcance:</p>
            <ul>
              <li><strong>Todos</strong>: incluye todos los boards a los que tienes acceso.</li>
              <li><strong>Compartidos conmigo</strong>: boards que te invitaron.</li>
              <li><strong>Colaborativos</strong>: tus boards que ya tienen invitados.</li>
            </ul>
            <p>Dentro de un board, el botón <strong>Filtros</strong> permite acotar las tarjetas visibles por etiqueta, prioridad o rango de fecha (vencidas / próximos 7 días).</p>
          </Section>

          <Section id="responsive" icon={Smartphone} title="Móvil y responsive">
            <p>La app está optimizada para móvil:</p>
            <ul>
              <li>El menú lateral aparece como cajón al tocar el icono hamburguesa.</li>
              <li>Las listas del board se desplazan horizontalmente con swipe.</li>
              <li>El detalle de tarjeta ocupa pantalla completa para hacer cómoda la edición.</li>
              <li>Tras navegar a un board, el cajón se cierra automáticamente.</li>
              <li>Los botones del header (Detalles, Compartir, Actividad) se adaptan al espacio disponible.</li>
            </ul>
          </Section>

          <Section id="theme" icon={Moon} title="Modo oscuro">
            <p>Alterna entre tema claro y oscuro con el icono de luna/sol en la cabecera. La preferencia queda guardada y se aplica al volver a entrar.</p>
          </Section>

          <Section id="shortcuts" icon={Keyboard} title="Atajos de teclado">
            <ul>
              <li><strong>Esc</strong>: cerrar diálogos o modales.</li>
              <li><strong>Enter</strong> al renombrar listas o tarjetas: guarda el cambio.</li>
              <li><strong>Espacio</strong> sobre una tarjeta: inicia o suelta el modo drag por teclado.</li>
              <li><strong>Flechas</strong> durante el drag por teclado: mueve la tarjeta entre listas o filas.</li>
              <li><strong>Tab</strong>: navega entre elementos accesibles.</li>
            </ul>
          </Section>

          <Section id="billing" icon={CreditCard} title="Planes y PayPal">
            <p>Sprintroll tiene un <strong>plan gratuito para siempre</strong>, sin tarjeta. Incluye acceso a todas las funciones con estos límites:</p>
            <ul>
              <li>Hasta <strong>2 workspaces</strong> por usuario.</li>
              <li>Hasta <strong>2 tableros</strong> en total, incluyendo tableros compartidos donde te invitaron.</li>
              <li>Hasta <strong>10 MB</strong> de adjuntos en tarjetas.</li>
            </ul>
            <p>Un banner en la parte superior muestra tu uso actual y se pinta naranja/rojo cuando te acercas o llegas al límite. Cuando pegas el tope, la app te invita a suscribirte para desbloquear más.</p>
            <ul>
              <li><strong>Pro</strong>: plan personal para freelancers y uso individual, con 50 MB de almacenamiento y workspaces/tableros ilimitados.</li>
              <li><strong>Corporativo</strong>: desde 3 usuarios, con 50 MB por miembro, panel de admin, estadísticas de accesos y chat interno. Descuento por pago anual.</li>
              <li>Los precios actuales se muestran en la landing page y en <strong>Configuración → Suscripción</strong>.</li>
            </ul>
            <p>El cobro se realiza con <strong>PayPal</strong>. Tras completar el pago, tu suscripción se activa automáticamente por verificación de webhook — sin demoras manuales. También se procesan reembolsos y cancelaciones de forma automática.</p>
          </Section>

          <Section id="privacy" icon={ShieldCheck} title="Privacidad y términos">
            <p>Tu información está protegida y cifrada en tránsito y en reposo. Al iniciar sesión por primera vez (o cuando actualizamos los términos), te pediremos aceptar la versión vigente de <strong>Términos y Condiciones</strong> y <strong>Aviso de Privacidad</strong>.</p>
            <ul>
              <li>Puedes consultarlos en cualquier momento desde el footer o en <a href="/terms" className="text-blue-600 dark:text-blue-300 hover:underline">/terms</a>.</li>
              <li>Cada vez que cambian, se te notifica al ingresar.</li>
              <li>Si tienes dudas o quieres ejercer derechos ARCO, escríbenos a <a href="mailto:hola@sprintroll.app" className="text-blue-600 dark:text-blue-300 hover:underline">hola@sprintroll.app</a>.</li>
            </ul>
          </Section>

          <footer className="border-t border-white/30 dark:border-white/10 pt-6 text-sm text-muted-foreground">
            ¿Te quedaste con dudas?{" "}
            <Link to="/support" data-testid="help-footer-support" className="text-blue-600 dark:text-blue-300 hover:underline font-medium">
              Abre un ticket de soporte
            </Link>{" "}
            o escríbenos a <a href="mailto:hola@sprintroll.app" className="text-blue-600 dark:text-blue-300 hover:underline">hola@sprintroll.app</a>.
          </footer>
        </article>
      </div>
    </div>
  );
}
